import { refreshSession } from '../api/client';
import { record } from '../diag/diag.svelte';
import { sessionStore } from './session-store';

/**
 * Renews the access token before it runs out, while the page is on screen, so the tap that would
 * have met an expired token does not pay a failed request, a refresh and a repeat on a slow phone
 * connection (employer report 2026-10-20). Tokens live 10 to 15 minutes and only in memory.
 *
 * Nothing runs while the page is hidden (a frozen page must not start a refresh it cannot finish);
 * coming back renews at once when the token is due. A failed refresh (no network) is tried again
 * shortly; one the server refuses ends the session, as a 401 does.
 */

/** Renew this long before the end, or a quarter of the lifetime if that is shorter. */
const MARGIN_MS = 90_000;
const RETRY_MS = 20_000;

/** How long the token lives (exp - iat from its payload, so a wrong device clock does not matter), or null. */
export function tokenLifetimeMs(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      iat?: unknown;
      exp?: unknown;
    };
    if (typeof claims.iat !== 'number' || typeof claims.exp !== 'number') return null;
    const ms = (claims.exp - claims.iat) * 1000;
    return ms > 0 ? ms : null;
  } catch {
    return null;
  }
}

/** Starts keeping the session fresh; the returned function stops it. */
export function startSessionKeeper(): () => void {
  let seen: string | null = null;
  let issuedAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const clear = () => {
    clearTimeout(timer);
    timer = undefined;
  };

  const renew = () => {
    clear();
    if (!sessionStore.get() || document.visibilityState !== 'visible') return;
    record('keeper', 'renew before expiry');
    // A success stores the new token, which plans the next renewal; a failure is retried.
    refreshSession().then(
      () => undefined,
      () => {
        timer = setTimeout(renew, RETRY_MS);
      },
    );
  };

  const plan = () => {
    clear();
    const session = sessionStore.get();
    if (!session) {
      seen = null;
      return;
    }
    if (session.accessToken !== seen) {
      seen = session.accessToken;
      issuedAt = Date.now();
    }
    if (document.visibilityState !== 'visible') return;
    const life = tokenLifetimeMs(session.accessToken);
    if (life === null) return;
    const due = issuedAt + life - Math.min(MARGIN_MS, life / 4);
    timer = setTimeout(renew, Math.max(0, due - Date.now()));
  };

  const unsubscribe = sessionStore.subscribe(plan);
  document.addEventListener('visibilitychange', plan);
  window.addEventListener('pageshow', plan);
  window.addEventListener('online', plan);
  plan();
  return () => {
    clear();
    unsubscribe();
    document.removeEventListener('visibilitychange', plan);
    window.removeEventListener('pageshow', plan);
    window.removeEventListener('online', plan);
  };
}
